import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IncomingCallGate } from '@/lib/incoming-call-gate';
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  Track,
  ConnectionState,
  type RemoteTrackPublication,
  type LocalTrackPublication,
  type RemoteTrack,
  type LocalAudioTrack,
  type LocalVideoTrack,
  AudioPresets,
  VideoPresets,
  ScreenSharePresets,
} from 'livekit-client';
import { apiRequest } from '@/lib/queryClient';
import { authedFetch } from '@/lib/authed-fetch';
import { ApiError, extractFriendly, parseApiErrorObject } from '@/lib/api-error';
import { primeAudioPlayback, wireAudioPlaybackWatcher } from '@/lib/call-audio-unblock';
import { createOutgoingCallStartGate } from '@/lib/outgoing-call-start-gate';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import {
  enumerateMediaDevices,
  getSavedDevicePreferences,
  getSkipPreCallLobby,
  groupDevices,
  isSpeakerSelectionSupported,
  saveDevicePreference,
  saveSkipPreCallLobby,
  type DeviceKind,
} from '@/hooks/use-media-devices';
import {
  describeMediaError,
  enableCameraOnRoom,
  enableMicrophoneOnRoom,
} from '@/lib/media-acquisition';

export interface CallParticipantSummary {
  userId: number;
  isInitiator: boolean;
  status: string;
  /** Server-formatted display name. Null when the user
   * record could not be resolved server-side. */
  name?: string | null;
}

/** Project chip context — null for direct chats with no project selected. */
export interface CallProjectContext {
  id: string;
  name: string | null;
  /** Base64/data-URL project thumbnail, when one is available. */
  thumbnail?: string | null;
}

/** Transcription state machine snapshot — Phase B (#160). No audio captured yet. */
export interface TranscriptionSegment {
  projectId: string;
  startedAt: string;
  pausedAt?: string;
  endedAt?: string;
}
export interface TranscriptionState {
  status: 'idle' | 'armed' | 'paused';
  projectId: string | null;
  segments: TranscriptionSegment[];
}

export interface CallSessionSummary {
  sessionId: string;
  channelId: string;
  type: 'audio' | 'video';
  status: string;
  /** 'internal' | 'client_share' (task #77). Client-share calls are joined on
   * the share page (/p/<token>), never through the internal accept flow. */
  origin?: string;
  /** Share token for client_share calls — target page for joining. */
  shareToken?: string | null;
  initiatorUserId: number | null;
  /** Server-formatted display name of the initiator (e.g. "Anna B."). Baked
   * into the broadcast so the incoming-call modal renders the real name with
   * no race against the /api/users query. */
  initiatorName: string;
  /** Initiator's avatar background color, if any. */
  initiatorColorCode: string | null;
  startedAt: string | Date;
  participants: CallParticipantSummary[];
  /** Project chip + transcription state (Phase B — #160). Optional on the
   * type so older payloads don't blow up; treated as null/idle when absent. */
  project?: CallProjectContext | null;
  transcription?: TranscriptionState;
  /** Phase C (#161) cost-cap reason marker. Present when daily Whisper
   * spend is over the cap and transcription is fail-closed for the day. */
  costCapExceededAt?: string | null;
  costCapTotalCents?: number | null;
  costCapMaxCents?: number | null;
}

interface IncomingCall {
  session: CallSessionSummary;
}

/** A client ringing from their project page (task #77). Accepting opens the
 * share page in a new tab — the call itself lives there, not in the PM tool. */
export interface IncomingClientCall {
  sessionId: string;
  shareToken: string;
  shareTitle?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  guestName: string;
  type: 'audio' | 'video';
}

/** State for the pre-call device-check lobby. Set by `startCall` (outbound only)
 * and consumed by `<PreCallLobby />`. Accepting an incoming ring deliberately
 * never populates this — the caller is already waiting and shouldn't be held
 * while the callee pokes at sliders. */
export interface PendingOutgoingCall {
  channelId: string;
  type: 'audio' | 'video';
  inviteeUserIds: number[];
  /** Originating project (Phase B — #160). Direct-chat callers leave undefined;
   * project-channel callers pass the project id from the channel row. */
  projectId?: string | null;
}

/** Pre-warmed local tracks acquired by the lobby and handed off to
 * `connectToRoom` so `room.connect()` can publish them immediately. */
export interface PreWarmedTracks {
  audioTrack?: LocalAudioTrack;
  videoTrack?: LocalVideoTrack;
  speakerDeviceId?: string;
  /** Room the lobby called `prepareConnection(url)` on. Reusing the same
   * instance is what actually delivers the warm-up benefit. */
  room?: Room;
}

/** Build a Room with our low-latency publish defaults. VP8 + 720p30 cap +
 * simulcast down to 180p + maintain-framerate degradation keeps the
 * encoder from building a multi-second send-side buffer on slow uplinks. */
export function createCallRoom(): Room {
  return new Room({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      audioPreset: AudioPresets.speech,
      dtx: true,
      red: true,
      videoCodec: 'vp8',
      videoEncoding: VideoPresets.h720.encoding,
      videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
      degradationPreference: 'maintain-framerate',
    },
  });
}

interface ActiveCall {
  session: CallSessionSummary;
  room: Room;
  identity: string;
  micEnabled: boolean;
  camEnabled: boolean;
  screenShareEnabled: boolean;
  /** Device IDs currently in use. `undefined` means "system default" — i.e.
   * the user has never explicitly picked a device for this kind, or the
   * saved preference was unavailable and we fell back. */
  micDeviceId?: string;
  camDeviceId?: string;
  speakerDeviceId?: string;
}

interface CallContextValue {
  incomingCall: IncomingCall | null;
  /** A client ringing from their project page (task #77). */
  incomingClientCall: IncomingClientCall | null;
  /** Opens the share page in a new tab to join the client call. */
  acceptClientCall: () => void;
  declineClientCall: () => Promise<void>;
  /** Outbound-call lobby state. Non-null while the user has clicked "Call"
   * but has not yet confirmed the device check. */
  pendingOutgoing: PendingOutgoingCall | null;
  activeCall: ActiveCall | null;
  remoteParticipants: RemoteParticipant[];
  /**
   * User IDs currently inside the server-side grace window — they've gone
   * quiet but the server hasn't yet marked them as left. Drives the
   * "Reconnecting…" indicator on participant tiles.
   */
  reconnectingUserIds: ReadonlySet<number>;
  /**
   * True when the local LiveKit Room is in its reconnecting state (i.e.
   * LiveKit is silently retrying our own connection underneath). Drives the
   * in-call "Reconnecting…" banner and disables track-toggle controls so the
   * user can't desync mic/cam state during an ICE restart.
   */
  localReconnecting: boolean;
  startCall: (params: { channelId: string; type: 'audio' | 'video'; inviteeUserIds: number[]; projectId?: string | null }) => Promise<void>;
  /** Explicitly start/join a scheduled calendar appointment. Saving the
   * appointment itself never creates a LiveKit room. */
  startCalendarCall: (eventId: string) => Promise<void>;
  /** Start the caller/recipient pair represented by one callback action. */
  startCalendarCallback: (actionId: string) => Promise<void>;
  /** Set or clear the call's project. Server-authoritative; the broadcast
   * round-trip updates `activeCall.session.project`. */
  setCallProject: (projectId: string | null) => Promise<void>;
  /** Drive the transcription state machine. Server validates the transition
   * and broadcasts the new state to every participant. */
  patchTranscription: (
    action:
      | { action: 'arm'; projectId: string; allParticipantsAgreed: true }
      | { action: 'pause' }
      | { action: 'resume' }
      | { action: 'stop' }
      | { action: 'switchProject'; projectId: string },
  ) => Promise<void>;
  /** True while a transcription PATCH is in flight. Drives the disabled +
   * spinner state on the transcription toggle so double-clicks can't fire
   * duplicate requests (task #317). */
  transcriptionPending: boolean;
  /** Record the current user's §201 StGB consent acknowledgement for THIS
   * session. Persisted on `call_session_participants.metadata` so re-arming
   * later in the same call doesn't re-prompt the same user. */
  /** Confirm the outbound lobby and actually place the call, publishing the
   * pre-warmed local tracks instead of re-acquiring them. */
  confirmOutgoingFromLobby: (tracks: PreWarmedTracks) => Promise<void>;
  /** Cancel the outbound lobby without placing the call. The lobby is
   * responsible for stopping any tracks it acquired. */
  cancelOutgoingFromLobby: () => void;
  acceptIncoming: () => Promise<void>;
  declineIncoming: () => Promise<void>;
  /** Join/rejoin a running INTERNAL call by session id (header activity
   * bar). Server authorizes: admins any call, team members calls of
   * their assigned projects. Client-share calls use the share page. */
  joinBySessionId: (sessionId: string) => Promise<void>;
  /** Re-surface the ringing modal for an internal call the server says we
   * are still invited to (websocket ring missed — broadcasts have no
   * replay). Server-authoritative: nothing is shown unless /api/calls/active
   * confirms `myStatus === 'invited'` for that session. Never auto-accepts. */
  resurfaceIncomingCall: (sessionId: string) => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCam: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  /**
   * Task #206 — true when *some* participant in the current call is
   * publishing a screen-share track (local or remote). Drives the enable
   * state of the manual "take screenshot now" camera button: the button
   * is only useful when there is in fact a screen-share frame to grab.
   */
  screenShareActiveInRoom: boolean;
  /**
   * Task #206 — manual one-shot screenshot. Pulls the current frame from
   * the active screen-share track (local preferred, otherwise the first
   * remote share), encodes a JPEG, and uploads it to the staging area.
   * Throws on failure so the caller can surface a toast.
   */
  captureScreenSnapshot: () => Promise<void>;
  leaveCall: () => Promise<void>;
  endCallForAll: () => Promise<void>;
  switchMicrophone: (deviceId: string) => Promise<void>;
  switchCamera: (deviceId: string) => Promise<void>;
  switchSpeaker: (deviceId: string) => Promise<void>;
}

const CallContext = createContext<CallContextValue | undefined>(undefined);

/**
 * How long the local LiveKit Room is allowed to sit in its reconnecting state
 * before we give up and tear the call down. Set deliberately a few seconds
 * shorter than the server's `LEAVE_GRACE_MS` (30s) so the user sees our
 * friendly "ended due to connection failure" UI just before the server-side
 * grace window forcibly removes them from the session.
 */
const LOCAL_RECONNECT_TIMEOUT_MS = 25_000;

export function useCall(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used within CallProvider');
  return ctx;
}

async function jsonOrThrow(res: Response): Promise<any> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // Throw a typed ApiError so parseApiError can read status + body
    // directly without re-parsing the message string.  The "NNN: friendly"
    // message format is preserved for any caller that still matches on it.
    const { friendly, body } = extractFriendly(text, res.statusText);
    throw new ApiError(res.status, friendly, body, text);
  }
  return res.json();
}

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const incomingGateRef = useRef(new IncomingCallGate());
  const [incomingClientCall, setIncomingClientCall] = useState<IncomingClientCall | null>(null);
  const [pendingOutgoing, setPendingOutgoing] = useState<PendingOutgoingCall | null>(null);
  const pendingOutgoingRef = useRef<PendingOutgoingCall | null>(null);
  // State drives the lobby render, while the ref must change in the same
  // handler turn to protect another shortcut/click before React commits.
  const setPendingOutgoingSynchronously = useCallback((next: PendingOutgoingCall | null) => {
    if (next) incomingGateRef.current.invalidate();
    pendingOutgoingRef.current = next;
    setPendingOutgoing(next);
  }, []);
  // Covers the complete outbound acquisition window: creating a lobby,
  // waiting inside it, and the skip-lobby POST/connect handshake.
  const outgoingStartGateRef = useRef(createOutgoingCallStartGate());
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
  const [reconnectingUserIds, setReconnectingUserIds] = useState<ReadonlySet<number>>(() => new Set());
  const [localReconnecting, setLocalReconnecting] = useState(false);
  const activeCallRef = useRef<ActiveCall | null>(null);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  // Task #317: in-flight guard for transcription PATCHes. The ref is the
  // synchronous guard (state updates are async); the state drives the
  // disabled + spinner render on the toggle button.
  const [transcriptionPending, setTranscriptionPending] = useState(false);
  const transcriptionPendingRef = useRef(false);
  // In-flight guards for the mic / cam toggle buttons. Without these, a
  // user who double-clicks (or mashes) the mic/cam icon while the previous
  // setMicrophoneEnabled / setCameraEnabled call is still resolving can
  // queue up overlapping enable+disable requests, leaving the LiveKit
  // track and our `micEnabled` / `camEnabled` state in opposite places
  // (and triggering duplicate error toasts on failure). Tracked in refs
  // rather than state because we need a synchronous read at the start of
  // the click handler — a state update wouldn't have applied yet by the
  // time the second click fires.
  const micTogglePendingRef = useRef(false);
  const camTogglePendingRef = useRef(false);
  // Calendar starts are server-idempotent as well, but this synchronous guard
  // avoids needless duplicate token/connect work from a local double click.
  const calendarStartPendingRef = useRef(false);
  // Task #206 — manual "take screenshot now" replaces the Phase D (#180)
  // 10 s auto-capture loop. There is no longer any background interval to
  // tear down; toggling screen share off requires no client follow-up.
  // `screenShareActiveInRoom` mirrors the live LiveKit publication state
  // (local + remote) so the camera button enables exactly when there is a
  // frame to grab.
  const [screenShareActiveInRoom, setScreenShareActiveInRoom] = useState(false);

  // Pending "we've been reconnecting too long, give up" timer. Lives in a ref
  // so it survives re-renders and can be cancelled from any code path that
  // ends the call (Reconnected, Disconnected, leaveCall, endCallForAll, the
  // call:ended push, etc.) without needing to thread it through state.
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const addReconnecting = useCallback((userId: number) => {
    setReconnectingUserIds((prev) => {
      if (prev.has(userId)) return prev;
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
  }, []);

  const removeReconnecting = useCallback((userId: number) => {
    setReconnectingUserIds((prev) => {
      if (!prev.has(userId)) return prev;
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  }, []);

  const cleanupRoom = useCallback(async () => {
    // Always cancel the give-up timer first so a stale firing can't try to
    // tear down a brand-new call started moments after this one ended.
    clearReconnectTimeout();
    setScreenShareActiveInRoom(false);
    const ac = activeCallRef.current;
    if (ac) {
      try { await ac.room.disconnect(); } catch {}
    }
    activeCallRef.current = null;
    setActiveCall(null);
    setRemoteParticipants([]);
    // Any reconnecting indicators only make sense while a call is active.
    setReconnectingUserIds((prev) => (prev.size === 0 ? prev : new Set()));
    setLocalReconnecting(false);
  }, [clearReconnectTimeout]);

  // Single source of truth for "tear down locally, then best-effort tell the
  // server we left." Used by both the user-initiated `leaveCall` and the
  // bounded reconnect-timeout path. Local teardown runs FIRST and is never
  // gated on the server round-trip — the timeout path specifically expects
  // the network to be down, so a fetch failure here must not stall cleanup.
  const cleanupAndNotifyLeave = useCallback(async (sessionId: string) => {
    await cleanupRoom();
    try {
      await apiRequest('POST', `/api/calls/${sessionId}/leave`);
    } catch (e: any) {
      console.warn('[Call] leave notify failed:', e);
    }
  }, [cleanupRoom]);

  const refreshParticipants = useCallback((room: Room) => {
    const remotes = Array.from(room.remoteParticipants.values());
    setRemoteParticipants(remotes);
    // Task #206 — recompute "is anyone sharing their screen" so the camera
    // button's enabled state is in sync with the room. Local share is
    // tracked via activeCall.screenShareEnabled (set in toggleScreenShare)
    // so this only inspects the remote participants.
    const remoteSharing = remotes.some(p => {
      try {
        const pub = p.getTrackPublication(Track.Source.ScreenShare);
        return !!pub?.track;
      } catch { return false; }
    });
    const localSharing = activeCallRef.current?.screenShareEnabled === true;
    setScreenShareActiveInRoom(remoteSharing || localSharing);
  }, []);

  const connectToRoom = useCallback(
    async (
      session: CallSessionSummary,
      url: string,
      token: string,
      identity: string,
      preWarmed?: PreWarmedTracks,
    ): Promise<void> => {
      // Reuse the lobby's pre-warmed Room when present (warm-up benefit)
      // or build a fresh one for the skip-lobby fast path.
      const room = preWarmed?.room ?? createCallRoom();

      // Task #179 audio-path observability — log every track publish /
      // subscribe / playback-status event so the next live two-browser test
      // can pinpoint which rung of the diagnostic ladder fails (capture →
      // publish → route → subscribe → play) without a code change. Logs
      // intentionally use a stable `[Call/audio]` prefix so they're easy
      // to filter in the browser devtools console.
      room
        .on(RoomEvent.ParticipantConnected, (p) => {
          console.info('[Call/audio] participantConnected', { identity: p.identity });
          refreshParticipants(room);
        })
        .on(RoomEvent.ParticipantDisconnected, (p) => {
          console.info('[Call/audio] participantDisconnected', { identity: p.identity });
          refreshParticipants(room);
        })
        .on(RoomEvent.LocalTrackPublished, (pub) => {
          console.info('[Call/audio] localTrackPublished', {
            kind: pub.kind,
            source: pub.source,
            muted: pub.isMuted,
            sid: pub.trackSid,
          });
        })
        .on(RoomEvent.LocalTrackUnpublished, (pub) => {
          console.info('[Call/audio] localTrackUnpublished', {
            kind: pub.kind,
            source: pub.source,
            sid: pub.trackSid,
          });
          // Task #206 — when the user stops screen-sharing via the
          // browser's native "Stop sharing" UI (i.e. outside our own
          // toggleScreenShare call), LiveKit unpublishes the track but
          // our React state (`activeCall.screenShareEnabled`) is never
          // flipped. The camera button then stays enabled forever even
          // though no one is sharing. Force the cleanup here:
          // recompute `screenShareActiveInRoom` AND clear the local
          // sharing flag so the gating boolean reflects reality.
          if (pub.source === Track.Source.ScreenShare) {
            setActiveCall((prev) =>
              prev && prev.screenShareEnabled ? { ...prev, screenShareEnabled: false } : prev,
            );
            refreshParticipants(room);
          }
        })
        .on(RoomEvent.TrackSubscribed, (_track, pub, participant) => {
          console.info('[Call/audio] trackSubscribed', {
            kind: pub.kind,
            source: pub.source,
            from: participant.identity,
            muted: pub.isMuted,
          });
          refreshParticipants(room);
        })
        .on(RoomEvent.TrackUnsubscribed, (_track, pub, participant) => {
          console.info('[Call/audio] trackUnsubscribed', {
            kind: pub.kind,
            source: pub.source,
            from: participant.identity,
          });
          refreshParticipants(room);
        })
        .on(RoomEvent.TrackMuted, (pub, participant) => {
          console.info('[Call/audio] trackMuted', {
            kind: pub.kind,
            source: pub.source,
            from: participant.identity,
          });
        })
        .on(RoomEvent.TrackUnmuted, (pub, participant) => {
          console.info('[Call/audio] trackUnmuted', {
            kind: pub.kind,
            source: pub.source,
            from: participant.identity,
          });
        })
        // ─── Autoplay-policy unblock ──────────────────────────────────────
        // Chromium / Safari refuse to play <audio> elements that haven't
        // been authorised by a recent user gesture. When LiveKit attaches
        // a remote audio track and the browser blocks playback, it sets
        // `room.canPlaybackAudio = false` and fires this event. Surface
        // a toast whose action button calls `room.startAudio()` from
        // INSIDE the click handler — that gesture is what the browser
        // wants. Without this, two participants connect successfully,
        // both publish mics, both subscribe to the other's track, and
        // yet hear silence — exactly the failure mode #179 was filed
        // to fix.
        .on(RoomEvent.Reconnecting, () => {
          // LiveKit is silently retrying our own connection. Surface a banner
          // so the local user knows things aren't frozen.
          if (activeCallRef.current?.session.sessionId !== session.sessionId) return;
          setLocalReconnecting(true);
          // Start the bounded give-up timer if one isn't already armed.
          // LiveKit can re-fire Reconnecting multiple times during a single
          // outage (e.g. ICE restart attempts) so we deliberately keep the
          // *first* timer running rather than resetting the deadline on
          // every event — otherwise the user could be held in reconnecting
          // limbo indefinitely.
          if (reconnectTimeoutRef.current !== null) return;
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            const ac = activeCallRef.current;
            // Bail if the call already ended (Reconnected, Disconnected,
            // leave, end-for-all, call:ended push, or a fresh call started
            // in the meantime — cleanupRoom clears the timer in all those
            // paths, but we double-check here to be safe).
            if (!ac || ac.session.sessionId !== session.sessionId) return;
            const sessionId = ac.session.sessionId;
            // Show the toast first so the user sees a clear reason for the
            // teardown, then run the shared cleanup-and-notify path. The
            // helper does local teardown BEFORE the network round-trip and
            // tolerates the leave POST failing — which it almost certainly
            // will, since we got here precisely because the network is down.
            toast({
              title: 'Call ended',
              description: `We couldn't reconnect after ${Math.round(LOCAL_RECONNECT_TIMEOUT_MS / 1000)} seconds. Please check your connection and try again.`,
              variant: 'destructive',
            });
            void cleanupAndNotifyLeave(sessionId);
          }, LOCAL_RECONNECT_TIMEOUT_MS);
        })
        .on(RoomEvent.Reconnected, () => {
          if (activeCallRef.current?.session.sessionId === session.sessionId) {
            clearReconnectTimeout();
            setLocalReconnecting(false);
          }
        })
        .on(RoomEvent.Disconnected, () => {
          if (activeCallRef.current?.session.sessionId === session.sessionId) {
            // If the room ultimately gives up while still in the reconnecting
            // state, make sure the banner doesn't linger past teardown.
            // cleanupRoom() also clears this (and the give-up timer), but we
            // set it explicitly first in case a future refactor makes
            // cleanup async-skippable.
            clearReconnectTimeout();
            setLocalReconnecting(false);
            cleanupRoom();
          }
        });

      try {
        await room.connect(url, token);
      } catch (e) {
        // Network failure, bad token, ICE failure, etc. The room never came
        // up so no tracks were published — but the lobby has already handed
        // us LIVE pre-warmed MediaStreamTracks. Stop them now or the camera
        // light stays on indefinitely. Re-throw so the caller can surface
        // a toast.
        try { preWarmed?.audioTrack?.stop(); } catch {}
        try { preWarmed?.videoTrack?.stop(); } catch {}
        throw e;
      }
      console.info('[Call/audio] room connected', {
        sessionId: session.sessionId,
        identity,
        url,
      });

      // Defensive autoplay-policy unblock. We're still inside the promise
      // chain kicked off by the user's "Accept" / "Join" click gesture, so
      // calling `room.startAudio()` here grants playback permission for
      // every remote audio element LiveKit will attach. The
      // `AudioPlaybackStatusChanged` watcher below is the long-tail safety
      // net for the cases where the gesture context was lost (e.g. tab
      // backgrounded during the LiveKit handshake).
      await primeAudioPlayback(room);

      wireAudioPlaybackWatcher(room, {
        onPlaybackBlocked: () => {
          toast({
            title: 'Audio paused by browser',
            description: 'Click to allow this call to play sound.',
            action: (
              <ToastAction
                altText="Enable call audio"
                onClick={() => {
                  void room.startAudio().catch((err) => {
                    console.warn('[Call/audio] startAudio failed', err);
                  });
                }}
              >
                Enable audio
              </ToastAction>
            ),
          });
        },
      });

      // Apply the user's saved device preferences (or the pre-warmed tracks
      // already acquired by the lobby). The pre-warmed branch publishes the
      // already-acquired track directly so the very first captured frame
      // uses the right hardware (no "switch tracks immediately after
      // publish" flash). The non-pre-warmed branch goes through the
      // shared `enableCameraOnRoom` / `enableMicrophoneOnRoom` helpers in
      // @/lib/media-acquisition — single source of truth for stale-device
      // pre-flight, error classification, and friendly UI messages.
      const saved = getSavedDevicePreferences();
      const fellBack: string[] = [];
      // Surface connect-time enable failures as a non-blocking info toast
      // so the user understands why they joined audio-only or muted, and
      // knows which icon to click to retry. Without this, the call window
      // just appears with a strikethrough camera and zero explanation.
      const enableFailures: Array<{ kind: 'camera' | 'microphone'; reason: ReturnType<typeof describeMediaError> }> = [];

      const preMic = preWarmed?.audioTrack;
      const preCam = preWarmed?.videoTrack;

      let micEnabled = true;
      let appliedMicId: string | undefined;
      if (preMic) {
        // Pre-warmed by the lobby — publish without re-running getUserMedia.
        // The lobby has already nailed down the device choice, so we skip
        // the saved-preference fallback dance entirely.
        try {
          await room.localParticipant.publishTrack(preMic, { source: Track.Source.Microphone });
          appliedMicId = preMic.mediaStreamTrack?.getSettings().deviceId;
        } catch (e) {
          console.warn('[Call] mic publish (pre-warmed) failed', e);
          micEnabled = false;
          try { preMic.stop(); } catch {}
        }
      } else {
        const result = await enableMicrophoneOnRoom(room, saved.audioinput);
        if (result.ok) {
          appliedMicId = result.deviceId;
          if (result.fellBackToDefault) fellBack.push('microphone');
        } else {
          console.warn('[Call] mic enable failed', result.reason, result.cause);
          micEnabled = false;
          enableFailures.push({ kind: 'microphone', reason: describeMediaError(result.reason, 'microphone') });
        }
      }

      // Camera (video calls only). Mirror the mic flow.
      let camEnabled = false;
      let appliedCamId: string | undefined;
      if (session.type === 'video') {
        if (preCam) {
          try {
            await room.localParticipant.publishTrack(preCam, { source: Track.Source.Camera });
            appliedCamId = preCam.mediaStreamTrack?.getSettings().deviceId;
            camEnabled = true;
          } catch (e) {
            console.warn('[Call] camera publish (pre-warmed) failed', e);
            try { preCam.stop(); } catch {}
          }
        } else {
          const result = await enableCameraOnRoom(room, saved.videoinput);
          if (result.ok) {
            appliedCamId = result.deviceId;
            camEnabled = true;
            if (result.fellBackToDefault) fellBack.push('camera');
          } else {
            console.warn('[Call] camera enable failed', result.reason, result.cause);
            enableFailures.push({ kind: 'camera', reason: describeMediaError(result.reason, 'camera') });
          }
        }
      } else if (preCam) {
        // Lobby acquired a video track for an audio-only call — defensive
        // cleanup so the camera light doesn't stay on.
        try { preCam.stop(); } catch {}
      }

      // Speaker output: must be applied AFTER connect/publish so LiveKit's
      // internal audio element registry exists and switchActiveDevice can
      // call setSinkId on each. Skip entirely on browsers without
      // setSinkId — the UI shows the menu disabled with a tooltip.
      // Prefer the lobby's pick (which the user just saw applied in their
      // preview) over the persisted preference for parity with mic/cam.
      const requestedSpeakerId = preWarmed?.speakerDeviceId ?? saved.audiooutput;
      let appliedSpeakerId = requestedSpeakerId;
      if (requestedSpeakerId && isSpeakerSelectionSupported()) {
        try {
          await room.switchActiveDevice('audiooutput', requestedSpeakerId);
          // Persist the lobby's pick so the next join (without a lobby)
          // also lands on the same speaker.
          saveDevicePreference('audiooutput', requestedSpeakerId);
        } catch (e: any) {
          // Treat any failure here as "device gone". switchActiveDevice
          // does not surface OverconstrainedError consistently so we don't
          // narrow on the error name.
          fellBack.push('speaker');
          saveDevicePreference('audiooutput', null);
          appliedSpeakerId = undefined;
          console.warn('[Call] speaker apply failed', e);
        }
      } else if (requestedSpeakerId && !isSpeakerSelectionSupported()) {
        // Not supported in this browser — leave the saved preference in
        // localStorage (it may apply on a different machine/browser) and
        // just don't surface a toast.
        appliedSpeakerId = undefined;
      }

      if (fellBack.length > 0) {
        toast({
          title: 'Saved device unavailable',
          description: `Using system default for: ${fellBack.join(', ')}.`,
        });
      }

      // One toast per failed device kind. We intentionally show these AFTER
      // `fellBack` so the user sees the recovery message first and the
      // actionable failure second — they're more likely to act on the most
      // recent toast.
      for (const failure of enableFailures) {
        toast({
          title: failure.reason.title,
          description: failure.reason.description,
          variant: 'destructive',
        });
      }

      const newActive: ActiveCall = {
        session,
        room,
        identity,
        micEnabled,
        camEnabled,
        screenShareEnabled: false,
        micDeviceId: appliedMicId,
        camDeviceId: appliedCamId,
        speakerDeviceId: appliedSpeakerId,
      };
      // A second shortcut can run before React commits setActiveCall. Mirror
      // this transition synchronously so startCall sees the just-connected
      // room instead of starting a different project in that narrow window.
      activeCallRef.current = newActive;
      incomingGateRef.current.invalidate();
      setIncomingCall(null);
      setIncomingClientCall(null);
      setActiveCall(newActive);
      refreshParticipants(room);
    },
    [cleanupRoom, cleanupAndNotifyLeave, refreshParticipants, clearReconnectTimeout, toast],
  );

  // Listen for inbound call signalling messages
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { type: string; data: any };
      if (!detail) return;
      const { type, data } = detail;
      switch (type) {
        case 'call:incoming': {
          // Ignore if we're already on a call, mid-lobby, or already have a
          // different incoming. The lobby case is important: hijacking the
          // outbound flow with a ringing modal would orphan the local
          // tracks acquired by the lobby.
          if (activeCallRef.current || incomingGateRef.current.busy) return;
          if (pendingOutgoingRef.current) return;
          setIncomingCall({ session: data as CallSessionSummary });
          break;
        }
        case 'call:client_incoming': {
          // Task #77: a client is calling from their project page. Never
          // hijack an active call or the outbound lobby — the missed-call
          // chat entry written by the server's ring timeout covers us.
          if (activeCallRef.current || incomingGateRef.current.busy) return;
          if (pendingOutgoingRef.current) return;
          const d = data as Partial<IncomingClientCall> & { sessionId?: string; shareToken?: string };
          if (d?.sessionId && d?.shareToken) {
            setIncomingClientCall({
              sessionId: d.sessionId,
              shareToken: d.shareToken,
              shareTitle: d.shareTitle ?? null,
              projectId: (d.projectId as string | null | undefined) ?? null,
              projectName: d.projectName ?? null,
              guestName: d.guestName || 'Client',
              type: d.type === 'audio' ? 'audio' : 'video',
            });
          }
          break;
        }
        case 'call:created': {
          // Multi-tab echo: another tab of mine started a call. Ignore (the originating tab is in it).
          break;
        }
        case 'call:participant_joined': {
          // Treat a fresh-join from a peer as an implicit "they're back" — if
          // we somehow missed the paired participant_reconnected push (e.g.
          // out-of-order WS delivery) the indicator still clears. Gate by
          // sessionId so events for a different call can't touch our state.
          const uid = data?.userId;
          const sid = data?.sessionId;
          const activeSid = activeCallRef.current?.session.sessionId;
          if (typeof uid === 'number' && sid && sid === activeSid) {
            removeReconnecting(uid);
          }
          break;
        }
        case 'call:participant_left':
        case 'call:participant_declined': {
          // Peer is gone for real — drop any reconnecting state for them so
          // the ghost tile / overlay can't linger. Gate by sessionId so a
          // stray event from another call can't clear unrelated state.
          const uid = data?.userId;
          const sid = data?.sessionId;
          const activeSid = activeCallRef.current?.session.sessionId;
          if (typeof uid === 'number' && sid && sid === activeSid) {
            removeReconnecting(uid);
          }
          break;
        }
        case 'call:participant_reconnecting': {
          const uid = data?.userId;
          const sid = data?.sessionId;
          const activeSid = activeCallRef.current?.session.sessionId;
          // Only show indicators for the call we're actually in (cross-call
          // traffic must not leak ghost tiles into the active call's UI).
          // Don't show a "Reconnecting…" indicator for ourselves — it would
          // be confusing (we're obviously here, looking at the UI).
          if (typeof uid === 'number' && uid !== user?.id && sid && sid === activeSid) {
            addReconnecting(uid);
          }
          break;
        }
        case 'call:participant_reconnected': {
          const uid = data?.userId;
          const sid = data?.sessionId;
          const activeSid = activeCallRef.current?.session.sessionId;
          if (typeof uid === 'number' && sid && sid === activeSid) {
            removeReconnecting(uid);
          }
          break;
        }
        case 'call:project_changed':
        case 'call:transcription_state': {
          // Server is the canonical owner of the project chip + transcription
          // state. We replace `activeCall.session` wholesale with the broadcast
          // payload (a full CallSummary) so chip, toggle and banner all stay
          // in lock-step. Gate by sessionId so traffic from a
          // different call can't touch our state.
          const incomingSession = data as CallSessionSummary | undefined;
          const sid = incomingSession?.sessionId;
          const activeSid = activeCallRef.current?.session.sessionId;
          if (incomingSession && sid && sid === activeSid) {
            setActiveCall((prev) => (prev ? { ...prev, session: incomingSession } : prev));
          }
          break;
        }
        case 'call:ended': {
          const sessionId = data?.sessionId as string;
          if (incomingCall && incomingCall.session.sessionId === sessionId) {
            setIncomingCall(null);
            toast({ title: 'Call ended', description: 'The caller ended the call.' });
          }
          if (incomingClientCall && incomingClientCall.sessionId === sessionId) {
            // Client hung up / ring timed out / a teammate picked it up.
            setIncomingClientCall(null);
          }
          if (activeCallRef.current?.session.sessionId === sessionId) {
            // cleanupRoom() already drops the reconnecting set, but we belt-
            // and-braces clear here too so a later regression can't leave
            // stale ghost-tile state behind across consecutive calls.
            setReconnectingUserIds((prev) => (prev.size === 0 ? prev : new Set()));
            cleanupRoom();
            toast({ title: 'Call ended', description: data?.reason === 'user_ended' ? 'The host ended the call.' : 'The call has ended.' });
          }
          break;
        }
      }
    };
    window.addEventListener('call:message', handler as EventListener);
    return () => window.removeEventListener('call:message', handler as EventListener);
  }, [cleanupRoom, incomingCall, incomingClientCall, toast, addReconnecting, removeReconnecting, user?.id]);

  /** Place an outbound call against the server. Internal — used by both the
   * "skip lobby" fast path and `confirmOutgoingFromLobby`. */
  const placeOutgoing = useCallback(
    async (
      params: { channelId: string; type: 'audio' | 'video'; inviteeUserIds: number[]; projectId?: string | null },
      preWarmed?: PreWarmedTracks,
    ) => {
      const res = await apiRequest('POST', '/api/calls', params);
      const body = await jsonOrThrow(res);
      await connectToRoom(body.session, body.livekit.url, body.livekit.token, body.livekit.identity, preWarmed);
    },
    [connectToRoom],
  );

  // Map a call-start failure to a user-facing description. `apiRequest` throws
  // a plain `TypeError: Failed to fetch` (or Safari's "Load failed") when the
  // request never reaches the server — a cold start, a dropped connection, or
  // the server briefly restarting. Blaming that on the call flow is confusing,
  // so we surface it as a network hint the user can act on (retry).
  const describeStartCallError = (e: any): string => {
    const msg: string = e?.message || 'Unknown error';
    if (/Failed to fetch|NetworkError|Load failed|ERR_NETWORK/i.test(msg)) {
      return 'Could not reach the server. Check your connection and try again.';
    }
    return msg;
  };

  const startCall = useCallback(
    async ({ channelId, type, inviteeUserIds, projectId }: { channelId: string; type: 'audio' | 'video'; inviteeUserIds: number[]; projectId?: string | null }) => {
      if (incomingGateRef.current.busy) return;
      if (activeCallRef.current) {
        toast({ title: 'Already in a call', description: 'Leave your current call first.', variant: 'destructive' });
        return;
      }
      if (pendingOutgoingRef.current || !outgoingStartGateRef.current.tryAcquire()) {
        // React has not necessarily rendered the lobby yet. The synchronous
        // ref gate prevents a second project from replacing the first
        // outbound flow during that gap and gives the caller clear feedback.
        toast({
          title: 'Call already starting',
          description: 'Finish or cancel the current device check before starting another call.',
          variant: 'destructive',
        });
        return;
      }
      // Fast path: user has opted out of the lobby. Acquire tracks the
      // legacy way (via setMicrophoneEnabled / setCameraEnabled inside
      // connectToRoom) using their saved device preferences. Always surface
      // a toast with an "undo" affordance — without it, the user has no UI
      // path to re-enable the lobby until follow-up #68 (Test devices)
      // ships, leaving them effectively locked out of pre-call checks.
      if (getSkipPreCallLobby()) {
        try {
          await placeOutgoing({ channelId, type, inviteeUserIds, projectId });
          toast({
            title: 'Skipped device check',
            description: 'Joined with your saved devices.',
            action: (
              <ToastAction
                altText="Show device check before next call"
                onClick={() => {
                  saveSkipPreCallLobby(false);
                  toast({
                    title: 'Device check re-enabled',
                    description: "You'll see the lobby again before your next outbound call.",
                  });
                }}
              >
                Show next time
              </ToastAction>
            ),
          });
        } catch (e: any) {
          toast({ title: 'Could not start call', description: describeStartCallError(e), variant: 'destructive' });
        } finally {
          // `connectToRoom` has synchronously installed activeCallRef on
          // success; failures must always release the gate for a retry.
          outgoingStartGateRef.current.release();
        }
        return;
      }
      // Lobby path: stash the intent and let `<PreCallLobby />` drive the
      // rest. The actual POST /api/calls only fires when the user clicks
      // "Join" inside the lobby — clicking "Cancel" never creates a
      // server-side session and so never rings the invitees.
      setPendingOutgoingSynchronously({ channelId, type, inviteeUserIds, projectId });
    },
    [placeOutgoing, setPendingOutgoingSynchronously, toast],
  );

  const startCalendarSource = useCallback(async (endpoint: string) => {
    if (activeCallRef.current) {
      throw new Error('Leave your current call first.');
    }
    if (calendarStartPendingRef.current) {
      throw new Error('This call is already being started.');
    }
    calendarStartPendingRef.current = true;
    try {
      // The protected start endpoint performs the durable source claim and
      // returns the exact same connection envelope as /calls and /accept.
      // Room setup remains centralized in connectToRoom.
      const res = await apiRequest('POST', endpoint);
      const body = await jsonOrThrow(res);
      await connectToRoom(
        body.session,
        body.livekit.url,
        body.livekit.token,
        body.livekit.identity,
      );
    } catch (e: any) {
      // The event/callback caller owns the user-facing message. Rejecting is
      // important: UI code must not invalidate/remove a request as though an
      // explicit start succeeded when the protected endpoint failed.
      const friendly = describeStartCallError(e);
      if (e instanceof Error && friendly === e.message) throw e;
      throw new Error(friendly);
    } finally {
      calendarStartPendingRef.current = false;
    }
  }, [connectToRoom]);

  const startCalendarCall = useCallback(
    (eventId: string) =>
      startCalendarSource(`/api/calendar/events/${encodeURIComponent(eventId)}/start`),
    [startCalendarSource],
  );

  const startCalendarCallback = useCallback(
    (actionId: string) =>
      startCalendarSource(`/api/calendar/call-actions/${encodeURIComponent(actionId)}/start`),
    [startCalendarSource],
  );

  /** Parse a thrown API error into its HTTP status + parsed JSON body.
   *
   * Reads `ApiError.status` / `ApiError.body` directly when the error was
   * thrown by `throwIfResNotOk` or `jsonOrThrow` (preferred — no string
   * re-parsing needed and the full structured body is preserved, including
   * `code`, `missing`, `reason` used by the transcription branches).
   *
   * Falls back to the legacy `/^(\d{3}):\s*([\s\S]*)$/` string parse so
   * any error that is not an ApiError (third-party, older paths) still
   * works. */
  const parseApiError = (e: any): { status: number | null; body: any } =>
    parseApiErrorObject(e);

  /** Server-side project chip update. Last write wins; the broadcast updates
   * every participant's view of the chip + transcription state (the server
   * also closes/opens transcription segments inside the same lock). */
  const setCallProject = useCallback(async (projectId: string | null) => {
    const ac = activeCallRef.current;
    if (!ac) return;
    try {
      await apiRequest('PATCH', `/api/calls/${ac.session.sessionId}/project`, { projectId });
    } catch (e: any) {
      // Task 172 Bug 2: surface 403 as a German "no access" toast so the chip
      // stays visually accurate when a participant tries to switch to a
      // project they don't have access to.
      const { status, body } = parseApiError(e);
      if (status === 403) {
        toast({
          title: 'No access to the project',
          description: body?.error ?? 'You do not have access to this project.',
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Could not switch project', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
  }, [toast]);

  const patchTranscription = useCallback(async (
    action:
      | { action: 'arm'; projectId: string; allParticipantsAgreed: true }
      | { action: 'pause' }
      | { action: 'resume' }
      | { action: 'stop' }
      | { action: 'switchProject'; projectId: string },
  ) => {
    const ac = activeCallRef.current;
    if (!ac) return;
    // Task #317: in-flight guard — a second invocation while a PATCH is
    // pending is a no-op, so double-clicks can't fire duplicate transitions.
    if (transcriptionPendingRef.current) return;
    transcriptionPendingRef.current = true;
    setTranscriptionPending(true);
    try {
      const res = await apiRequest('PATCH', `/api/calls/${ac.session.sessionId}/transcription`, action);
      // Task #317: apply the canonical session summary from the HTTP response
      // immediately instead of relying solely on the WS broadcast. The
      // broadcast is dropped (not replayed) while the socket reconnects,
      // which left the toggle stale in prod even though the server armed.
      // The server sends the broadcast BEFORE this response, so any broadcast
      // arriving on a live socket after this apply is equal-or-newer; a rare
      // concurrent-actor flicker self-heals when that broadcast lands.
      try {
        const body = await res.json();
        const incoming = body?.session as CallSessionSummary | undefined;
        if (incoming && incoming.sessionId === activeCallRef.current?.session.sessionId) {
          setActiveCall((prev) => (prev ? { ...prev, session: incoming } : prev));
        }
      } catch {
        // Malformed/empty body — broadcast (or the 409 resync path on the
        // next click) remains the fallback; never fail a successful PATCH.
      }
    } catch (e: any) {
      // Task 172 Bugs 3+4: parse the structured error body so the toast
      // names the actual cause + the toggle visually reverts (the server
      // already rolled back its canonical state, so the next broadcast
      // restores idle). 409 = engine_unavailable, 502 = egress_start_failed.
      const { status, body } = parseApiError(e);
      const code = body?.code as string | undefined;
      if (status === 409 && code === 'engine_unavailable') {
        const missing = Array.isArray(body?.missing) && body.missing.length > 0
          ? ` Missing configuration: ${body.missing.join(', ')}.`
          : '';
        toast({
          title: 'Transcription not available',
          description: `The transcription engine is not configured.${missing}`,
          variant: 'destructive',
        });
        return;
      }
      if (status === 502 && code === 'egress_start_failed') {
        toast({
          title: 'Recording could not be started',
          description: body?.reason === 'egress_limit_reached'
            ? 'The recording service is still busy with a previous recording. Please try again in a minute.'
            : body?.reason
              ? `Reason: ${body.reason}. Please try again.`
              : 'Please try again.',
          variant: 'destructive',
        });
        return;
      }
      if (status === 409 && code === 'invalid_transition') {
        // Client state diverged from the server (double-click race or a
        // missed broadcast). Resync silently — this is quiet self-recovery,
        // not a user-facing failure (task #317). Only toast if the resync
        // itself fails and the user is genuinely stuck.
        try {
          const res = await apiRequest('GET', '/api/calls/active');
          const body = await res.json();
          const incoming = body?.session as CallSessionSummary | undefined;
          if (incoming && incoming.sessionId === activeCallRef.current?.session.sessionId) {
            setActiveCall((prev) => (prev ? { ...prev, session: incoming } : prev));
          }
        } catch {
          toast({
            title: 'Transcription state out of sync',
            description: 'The view could not be refreshed. Please try again.',
            variant: 'destructive',
          });
        }
        return;
      }
      if (status === 402 && code === 'cost_cap_exceeded') {
        toast({
          title: 'Daily limit reached',
          description: 'Transcription is paused for today.',
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Could not update transcription', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      // Task #317: always release the guard — no failure path may strand the
      // toggle in a permanently disabled state.
      transcriptionPendingRef.current = false;
      setTranscriptionPending(false);
    }
  }, [toast]);

  const confirmOutgoingFromLobby = useCallback(
    async (preWarmed: PreWarmedTracks) => {
      const params = pendingOutgoingRef.current;
      if (!params) return;
      // Clear the lobby state immediately so the modal unmounts and the
      // user sees the in-call window appear once `connectToRoom` resolves.
      setPendingOutgoingSynchronously(null);
      try {
        await placeOutgoing(params, preWarmed);
      } catch (e: any) {
        // Lobby handed off ownership; on failure we own teardown.
        preWarmed.audioTrack?.stop();
        preWarmed.videoTrack?.stop();
        void preWarmed.room?.disconnect();
        toast({ title: 'Could not start call', description: describeStartCallError(e), variant: 'destructive' });
      } finally {
        // Keep the same synchronous guard across the lobby handoff and
        // release it regardless of room/API success so a failed join retries.
        outgoingStartGateRef.current.release();
      }
    },
    [placeOutgoing, setPendingOutgoingSynchronously, toast],
  );

  const cancelOutgoingFromLobby = useCallback(() => {
    // The lobby itself stops any tracks it acquired — we just clear state
    // so the modal unmounts. Splitting the responsibility this way keeps
    // the context out of the AudioContext / MediaStreamTrack lifecycle.
    setPendingOutgoingSynchronously(null);
    outgoingStartGateRef.current.release();
  }, [setPendingOutgoingSynchronously]);

  const acceptIncoming = useCallback(async () => {
    if (!incomingCall || activeCallRef.current || outgoingStartGateRef.current.isAcquired()
        || !incomingGateRef.current.begin()) return;
    const session = incomingCall.session;
    setIncomingCall(null);
    // Per product spec: accepting a ringing call NEVER goes through the
    // lobby. The caller is already waiting on us; routing the callee
    // through a device check would create awkward dead air. Saved device
    // preferences from #65 are applied inside connectToRoom, with the
    // usual OverconstrainedError fallback to system defaults.
    try {
      const res = await apiRequest('POST', `/api/calls/${session.sessionId}/accept`);
      const body = await jsonOrThrow(res);
      await connectToRoom(body.session, body.livekit.url, body.livekit.token, body.livekit.identity);
    } catch (e: any) {
      toast({ title: 'Could not join call', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      incomingGateRef.current.finish();
    }
  }, [incomingCall, connectToRoom, toast]);

  const joinBySessionId = useCallback(async (sessionId: string) => {
    if (activeCallRef.current || outgoingStartGateRef.current.isAcquired() || !incomingGateRef.current.begin()) return;
    setIncomingCall(null);
    try {
      const res = await apiRequest('POST', `/api/calls/${sessionId}/accept`);
      const body = await jsonOrThrow(res);
      await connectToRoom(body.session, body.livekit.url, body.livekit.token, body.livekit.identity);
    } catch (e: any) {
      toast({ title: 'Could not join call', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      incomingGateRef.current.finish();
    }
  }, [connectToRoom, toast]);

  const resurfaceIncomingCall = useCallback(async (sessionId: string) => {
    // Same guards as the live 'call:incoming' handler: never hijack an
    // active call or the outbound lobby.
    if (activeCallRef.current || pendingOutgoingRef.current || incomingGateRef.current.busy) return;
    const generation = incomingGateRef.current.snapshot();
    try {
      const res = await apiRequest('GET', '/api/calls/active');
      const body = await res.json();
      const summary = body?.session as CallSessionSummary | undefined;
      if (!summary || summary.sessionId !== sessionId) return;
      if (body.myStatus !== 'invited' || summary.origin === 'client_share') return;
      if (activeCallRef.current || pendingOutgoingRef.current || !incomingGateRef.current.isCurrent(generation)) return;
      // Functional update: an already-ringing modal (live event won the
      // race) is never replaced.
      setIncomingCall(prev => prev ?? { session: summary });
    } catch {
      // Server-authoritative check failed: nothing to resurface.
    }
  }, []);

  const declineIncoming = useCallback(async () => {
    if (!incomingCall || !incomingGateRef.current.begin()) return;
    const session = incomingCall.session;
    setIncomingCall(null);
    try {
      await apiRequest('POST', `/api/calls/${session.sessionId}/decline`);
    } catch (e: any) {
      console.warn('[Call] decline failed:', e);
    } finally {
      incomingGateRef.current.finish();
    }
  }, [incomingCall]);

  /** Task #77: accept a client call — the call runs on the share page, so we
   * open it in a new tab; the page auto-joins via its `?call=` param. */
  const acceptClientCall = useCallback(() => {
    const c = incomingClientCall;
    if (!c) return;
    setIncomingClientCall(null);
    // ?nointro skips the share page's brand splash — the member should
    // land directly in the call join flow, not watch a logo reveal.
    window.open(`/p/${c.shareToken}?call=${encodeURIComponent(c.sessionId)}&nointro`, '_blank', 'noopener');
  }, [incomingClientCall]);

  const declineClientCall = useCallback(async () => {
    const c = incomingClientCall;
    if (!c) return;
    setIncomingClientCall(null);
    try {
      await apiRequest('POST', `/api/calls/${c.sessionId}/decline`);
    } catch (e: any) {
      console.warn('[Call] client-call decline failed:', e);
    }
  }, [incomingClientCall]);

  const toggleMic = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) return;
    // Drop the click if a previous toggle is still in flight — see comment
    // on `micTogglePendingRef` for why this matters.
    if (micTogglePendingRef.current) return;
    micTogglePendingRef.current = true;
    const next = !ac.micEnabled;
    try {
      if (next) {
        // Re-acquire path. Goes through the shared helper so the user sees
        // the same friendly "Microphone is in use" / "blocked" / "not
        // found" toasts they'd see at connect time, instead of a raw
        // browser string. Pass the in-use deviceId so flipping the toggle
        // doesn't silently revert them to the system default mic.
        const result = await enableMicrophoneOnRoom(ac.room, ac.micDeviceId);
        if (result.ok) {
          setActiveCall((prev) =>
            prev ? { ...prev, micEnabled: true, micDeviceId: result.deviceId ?? prev.micDeviceId } : prev,
          );
        } else {
          const { title, description } = describeMediaError(result.reason, 'microphone');
          toast({ title, description, variant: 'destructive' });
        }
      } else {
        try {
          await ac.room.localParticipant.setMicrophoneEnabled(false);
          setActiveCall((prev) => (prev ? { ...prev, micEnabled: false } : prev));
        } catch (e: any) {
          // Disabling rarely fails, but if it does we still want a friendly
          // message rather than a raw browser string.
          toast({ title: 'Mic error', description: e?.message || 'Unknown error', variant: 'destructive' });
        }
      }
    } finally {
      micTogglePendingRef.current = false;
    }
  }, [toast]);

  const toggleCam = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) return;
    if (ac.session.type !== 'video') return;
    // Drop the click if a previous toggle is still in flight — see comment
    // on `camTogglePendingRef` for why this matters.
    if (camTogglePendingRef.current) return;
    camTogglePendingRef.current = true;
    const next = !ac.camEnabled;
    try {
      if (next) {
        // Re-acquire path. Goes through the shared helper so the user sees
        // a classified, actionable error ("Camera is in use" → close
        // Zoom/Teams) instead of "Camera error: Timeout starting video
        // source" which most users have no way to act on.
        const result = await enableCameraOnRoom(ac.room, ac.camDeviceId);
        if (result.ok) {
          setActiveCall((prev) =>
            prev ? { ...prev, camEnabled: true, camDeviceId: result.deviceId ?? prev.camDeviceId } : prev,
          );
        } else {
          const { title, description } = describeMediaError(result.reason, 'camera');
          toast({ title, description, variant: 'destructive' });
        }
      } else {
        try {
          await ac.room.localParticipant.setCameraEnabled(false);
          setActiveCall((prev) => (prev ? { ...prev, camEnabled: false } : prev));
        } catch (e: any) {
          toast({ title: 'Camera error', description: e?.message || 'Unknown error', variant: 'destructive' });
        }
      }
    } finally {
      camTogglePendingRef.current = false;
    }
  }, [toast]);

  const toggleScreenShare = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) return;
    if (ac.session.type !== 'video') return;
    const next = !ac.screenShareEnabled;
    if (next && typeof navigator !== 'undefined' && !navigator.mediaDevices?.getDisplayMedia) {
      const ua = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
      toast({
        title: 'Screen sharing not available',
        description: isIOS
          ? 'iOS and iPadOS browsers do not support screen sharing. Please join from a desktop browser to share your screen.'
          : 'Your browser does not support screen sharing. Try the latest Chrome, Edge, Firefox or Safari on desktop.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await ac.room.localParticipant.setScreenShareEnabled(
        next,
        next
          ? {
              resolution: { width: 1920, height: 1080, frameRate: 15 },
              video: { displaySurface: 'monitor' },
              // Surface the browser's "Share audio" checkbox alongside the
              // screen picker. Chrome/Edge honour this for tab and window
              // captures; Firefox and Safari ignore it and the share still
              // succeeds without audio. If the user leaves the box
              // unchecked, getDisplayMedia simply returns no audio track —
              // no error, no fallback needed.
              audio: true,
              // Hint Chrome to also offer the OS-level system audio
              // checkbox when the user picks a full screen on supported
              // platforms (Windows/ChromeOS). Browsers without support
              // ignore this field.
              systemAudio: 'include',
            }
          : undefined,
        next
          ? {
              videoCodec: 'vp9',
              videoEncoding: ScreenSharePresets.h1080fps15.encoding,
              scalabilityMode: 'L3T3_KEY',
              degradationPreference: 'maintain-resolution',
              // Music-quality preset for the screen-audio track so a
              // YouTube clip / music demo doesn't get crushed by the
              // speech-tuned default. Mic stays on the speech preset
              // because it's published as a separate track.
              audioPreset: AudioPresets.musicHighQuality,
              red: false,
              dtx: false,
            }
          : undefined,
      );
      setActiveCall((prev) => (prev ? { ...prev, screenShareEnabled: next } : prev));
      // Task #206 — manual screenshots replace the auto-loop. We only need
      // to refresh the room-share signal so the camera button enables/
      // disables in lockstep with the local publication state. Remote
      // shares are picked up via refreshParticipants on TrackSubscribed.
      setScreenShareActiveInRoom((prev) => {
        if (next) return true;
        // Local turned off — recompute from remotes.
        try {
          const remoteSharing = Array.from(ac.room.remoteParticipants.values()).some(p => {
            const pub = p.getTrackPublication(Track.Source.ScreenShare);
            return !!pub?.track;
          });
          return remoteSharing;
        } catch { return prev; }
      });
    } catch (e: any) {
      const name: string = e?.name || '';
      const msg: string = e?.message || 'Unknown error';
      // Screen capture (getDisplayMedia) is blocked by permissions policy when
      // the app runs inside a cross-origin iframe (e.g. an embedded preview).
      // Detect that first so we give actionable guidance instead of blaming the
      // device. Comparing window identities is allowed cross-origin; accessing
      // properties is not, so we guard with try/catch and assume embedded.
      const embedded = (() => {
        try { return window.self !== window.top; } catch { return true; }
      })();
      const permissionish = /getDisplayMedia|not supported|NotAllowedError|Permission denied/i.test(`${name} ${msg}`);
      let friendly = msg;
      if (embedded && permissionish) {
        friendly = 'Screen sharing is blocked when the app runs inside an embedded preview. Open the app in its own browser tab, then try sharing again.';
      } else if (permissionish) {
        friendly = 'Screen share was blocked or cancelled. Allow screen recording for your browser (on macOS: System Settings → Privacy & Security → Screen Recording), then try again.';
      }
      toast({ title: 'Screen share error', description: friendly, variant: 'destructive' });
    }
  }, [toast]);

  /**
   * Phase D (#180): grab a JPEG frame from the local screen-share video
   * track every SNAPSHOT_INTERVAL_MS and POST it to the server. The browser
   * gives us the captured display as a MediaStreamTrack; we copy each frame
   * into a hidden <canvas> via drawImage and pull the JPEG bytes via
   * toBlob('image/jpeg', 0.7). On a 202 cap-reached we stop the loop so we
   * don't pound the server forever; transient errors are logged but don't
   * stop the loop (next tick will retry).
   */
  /**
   * Task #206 — manual "take screenshot now". Grabs a single JPEG frame
   * from whichever screen-share track is currently live in the room
   * (local preferred, otherwise the first remote share) and POSTs it to
   * the staging endpoint. The server later promotes accepted frames into
   * the per-pulse Dropbox folder. Throws on failure so the caller can
   * surface a toast — there is no retry loop.
   */
  const captureScreenSnapshot = useCallback(async () => {
    if (typeof document === 'undefined') throw new Error('No browser context');
    const ac = activeCallRef.current;
    if (!ac) throw new Error('No active call');
    const sessionId = ac.session.sessionId;

    // Prefer the local screen-share track; fall back to the first remote
    // participant who is publishing one.
    let mst: MediaStreamTrack | undefined;
    const localPub: LocalTrackPublication | undefined =
      ac.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    mst = localPub?.track?.mediaStreamTrack;
    if (!mst) {
      for (const rp of Array.from(ac.room.remoteParticipants.values())) {
        const pub = rp.getTrackPublication(Track.Source.ScreenShare);
        const track = pub?.track?.mediaStreamTrack;
        if (track) { mst = track; break; }
      }
    }
    if (!mst) throw new Error('Nobody is sharing their screen right now');

    // Off-DOM <video>+<canvas>. Same pattern as the legacy auto-loop but
    // built fresh per click — no refs survive between captures.
    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([mst]);
    try { await video.play(); } catch { /* drawImage can still work once metadata is ready */ }

    // Wait briefly for the first frame so videoWidth/Height are set.
    await new Promise<void>((resolve) => {
      if (video.videoWidth > 0 && video.videoHeight > 0) return resolve();
      const onReady = () => { video.removeEventListener('loadedmetadata', onReady); resolve(); };
      video.addEventListener('loadedmetadata', onReady);
      // Hard timeout so a frozen track doesn't hang the button forever.
      setTimeout(() => resolve(), 1500);
    });

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      try { video.srcObject = null; } catch {}
      throw new Error('Screen content could not be read');
    }

    const MAX = 1280;
    const scale = Math.min(1, MAX / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      try { video.srcObject = null; } catch {}
      throw new Error('Canvas not available');
    }
    ctx.drawImage(video, 0, 0, cw, ch);
    // Stamp capturedAt at the exact drawImage moment (server clamps to a
    // ±30 s window relative to session start; misbehaving clocks fall
    // back to server NOW() with a clock_skew warning).
    const capturedAtMs = Date.now();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.7),
    );
    try { video.srcObject = null; } catch {}
    if (!blob) throw new Error('JPEG encoding failed');

    const fd = new FormData();
    fd.append('snapshot', blob, 'snapshot.jpg');
    fd.append('capturedAt', String(capturedAtMs));
    const res = await authedFetch(`/api/calls/${sessionId}/screen-snapshots`, {
      method: 'POST',
      body: fd,
    });
    if (res.status === 202) throw new Error('Limit of 30 captures per call reached');
    if (res.status === 409) throw new Error('Storage not configured');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const { friendly, body } = extractFriendly(text, res.statusText);
      throw new ApiError(res.status, `Upload failed (${res.status}): ${friendly}`, body, text);
    }
  }, []);

  const leaveCall = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) return;
    await cleanupAndNotifyLeave(ac.session.sessionId);
  }, [cleanupAndNotifyLeave]);

  const endCallForAll = useCallback(async () => {
    const ac = activeCallRef.current;
    if (!ac) return;
    const sessionId = ac.session.sessionId;
    await cleanupRoom();
    try {
      await apiRequest('POST', `/api/calls/${sessionId}/end`);
    } catch (e: any) {
      toast({ title: 'Could not end call', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
  }, [cleanupRoom, toast]);

  // Shared switch path for mic/cam/speaker. LiveKit's room.switchActiveDevice
  // republishes the affected track (audioinput/videoinput) or iterates the
  // SDK's internal audio-element registry and calls setSinkId on each
  // (audiooutput) — so we don't reimplement either of those manually.
  // On success we persist the choice and update the activeCall snapshot so
  // the dropdown checkmark stays accurate. On failure we revert nothing on
  // the LiveKit side (the previous device stays active) and show an error
  // toast — the indicator naturally stays on the previous selection.
  const switchDevice = useCallback(async (kind: DeviceKind, deviceId: string, label: string) => {
    const ac = activeCallRef.current;
    if (!ac) return;
    // Belt-and-braces: the UI also disables the trigger during reconnect,
    // but cover programmatic calls too — switching a track requires a
    // renegotiation that LiveKit can't perform mid-reconnect.
    if (localReconnecting) return;
    if (kind === 'audiooutput' && !isSpeakerSelectionSupported()) {
      toast({
        title: 'Speaker selection unavailable',
        description: 'Your browser does not support changing the audio output device.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await ac.room.switchActiveDevice(kind, deviceId);
      saveDevicePreference(kind, deviceId);
      setActiveCall((prev) => {
        if (!prev) return prev;
        if (kind === 'audioinput') return { ...prev, micDeviceId: deviceId };
        if (kind === 'videoinput') return { ...prev, camDeviceId: deviceId };
        return { ...prev, speakerDeviceId: deviceId };
      });
      toast({ title: `${label} switched` });
    } catch (e: any) {
      toast({
        title: `Could not switch ${label.toLowerCase()}`,
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [localReconnecting, toast]);

  const switchMicrophone = useCallback(
    (deviceId: string) => switchDevice('audioinput', deviceId, 'Microphone'),
    [switchDevice],
  );
  const switchCamera = useCallback(
    (deviceId: string) => switchDevice('videoinput', deviceId, 'Camera'),
    [switchDevice],
  );
  const switchSpeaker = useCallback(
    (deviceId: string) => switchDevice('audiooutput', deviceId, 'Speaker'),
    [switchDevice],
  );

  // When a call is active, proactively release the camera/mic and disconnect
  // from LiveKit before the tab is torn down. We use `pagehide` (not
  // `beforeunload`) because it fires reliably on mobile Safari/Chrome and on
  // bfcache navigation. Track-stop must run synchronously before any awaits —
  // the browser may not allow the async disconnect to complete, but stopping
  // the MediaStreamTracks is what actually turns the camera light off and
  // releases the mic immediately.
  useEffect(() => {
    if (!activeCall) return;
    const handler = () => {
      const ac = activeCallRef.current;
      if (!ac) return;
      try {
        // livekit-client v2 exposes `trackPublications` on LocalParticipant.
        const pubs = Array.from(ac.room.localParticipant.trackPublications.values());
        for (const pub of pubs) {
          try { pub.track?.stop(); } catch {}
          // Belt-and-braces: also stop the underlying MediaStreamTrack so a
          // future SDK rename can't quietly leave the camera light on.
          try { pub.track?.mediaStreamTrack?.stop(); } catch {}
        }
      } catch {}
      // Fire-and-forget: tells the LiveKit room we've gone so other
      // participants see us drop straight away rather than after a 30s timeout.
      try { void ac.room.disconnect(); } catch {}
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [activeCall]);

  // Reconnect to in-flight call on mount (e.g. browser refresh).
  // - If the user was already 'active' in the call, silently rejoin via /accept (fresh token).
  // - If the user was still 'invited' (ringing), surface the IncomingCallModal — never auto-accept.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const generation = incomingGateRef.current.snapshot();
    (async () => {
      try {
        const res = await apiRequest('GET', '/api/calls/active');
        const body = await res.json();
        if (cancelled || !body?.session || activeCallRef.current || pendingOutgoingRef.current
            || !incomingGateRef.current.isCurrent(generation)) return;
        const summary = body.session as CallSessionSummary;
        if (summary.origin === 'client_share') {
          // Task #77: client-share calls run in the share-page tab. Never
          // auto-/accept here (the server rejects it); a still-ringing invite
          // resurfaces as the client-call modal instead.
          if (body.myStatus === 'invited' && summary.shareToken) {
            setIncomingClientCall({
              sessionId: summary.sessionId,
              shareToken: summary.shareToken,
              projectId: summary.project?.id ?? null,
              projectName: summary.project?.name ?? null,
              guestName: summary.initiatorName || 'Client',
              type: summary.type,
            });
          }
          return;
        }
        if (body.myStatus === 'active') {
          if (!incomingGateRef.current.begin()) return;
          try {
            const acceptRes = await apiRequest('POST', `/api/calls/${body.session.sessionId}/accept`);
            const acceptBody = await jsonOrThrow(acceptRes);
            if (!cancelled) {
              await connectToRoom(acceptBody.session, acceptBody.livekit.url, acceptBody.livekit.token, acceptBody.livekit.identity);
            }
          } finally {
            incomingGateRef.current.finish();
          }
        } else if (body.myStatus === 'invited') {
          if (!cancelled) setIncomingCall({ session: body.session as CallSessionSummary });
        }
      } catch {
        // No active call or reconnect failed silently
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, connectToRoom]);

  const value = useMemo<CallContextValue>(() => ({
    incomingCall,
    incomingClientCall,
    acceptClientCall,
    declineClientCall,
    pendingOutgoing,
    activeCall,
    remoteParticipants,
    reconnectingUserIds,
    localReconnecting,
    startCall,
    startCalendarCall,
    startCalendarCallback,
    confirmOutgoingFromLobby,
    cancelOutgoingFromLobby,
    acceptIncoming,
    declineIncoming,
    joinBySessionId,
    resurfaceIncomingCall,
    toggleMic,
    toggleCam,
    toggleScreenShare,
    screenShareActiveInRoom,
    captureScreenSnapshot,
    leaveCall,
    endCallForAll,
    switchMicrophone,
    switchCamera,
    switchSpeaker,
    setCallProject,
    patchTranscription,
    transcriptionPending,
  }), [
    incomingCall,
    incomingClientCall,
    acceptClientCall,
    declineClientCall,
    pendingOutgoing,
    activeCall,
    remoteParticipants,
    reconnectingUserIds,
    localReconnecting,
    startCall,
    startCalendarCall,
    startCalendarCallback,
    confirmOutgoingFromLobby,
    cancelOutgoingFromLobby,
    acceptIncoming,
    declineIncoming,
    joinBySessionId,
    resurfaceIncomingCall,
    toggleMic,
    toggleCam,
    toggleScreenShare,
    screenShareActiveInRoom,
    captureScreenSnapshot,
    leaveCall,
    endCallForAll,
    switchMicrophone,
    switchCamera,
    switchSpeaker,
    setCallProject,
    patchTranscription,
    transcriptionPending,
  ]);

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export type { RemoteParticipant, RemoteTrack, RemoteTrackPublication, LocalTrackPublication };
export { Track, ConnectionState };
